import { useState, useCallback } from "react";
import { subscriptionService } from "@/services/subscription.service";
import type {
  FamilySubscription,
  Plan,
  PurchaseHistoryResponse,
} from "@/types/subscription";

export function useSubscription() {
  const [subscription, setSubscription] = useState<FamilySubscription | null>(
    null
  );
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCurrentSubscription = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await subscriptionService.getCurrentSubscription();
      setSubscription(data);
      return data;
    } catch (err: any) {
      const errorMsg = err?.message || "Error fetching subscription";
      setError(errorMsg);
      setSubscription(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await subscriptionService.getPlans();
      setPlans(data);
      return data;
    } catch (err: any) {
      const errorMsg = err?.message || "Error fetching plans";
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSubscriptionWithPlan = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [currentSub, plansData] = await Promise.all([
        subscriptionService.getCurrentSubscription(),
        subscriptionService.getPlans(),
      ]);

      const matchingPlan = plansData.find((p) => p.id === currentSub.planId);

      const subscriptionWithPlan = {
        ...currentSub,
        plan: matchingPlan,
      };

      setSubscription(subscriptionWithPlan);
      setPlans(plansData);
      return subscriptionWithPlan;
    } catch (err: any) {
      const errorMsg = err?.message || "Error fetching subscription";
      setError(errorMsg);
      setSubscription(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const cancelSubscription = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await subscriptionService.cancelSubscription();
      // Refresh subscription after cancellation
      await fetchCurrentSubscription();
    } catch (err: any) {
      const errorMsg = err?.message || "Error cancelling subscription";
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [fetchCurrentSubscription]);

  return {
    subscription,
    plans,
    loading,
    error,
    fetchCurrentSubscription,
    fetchPlans,
    fetchSubscriptionWithPlan,
    cancelSubscription,
  };
}

export function usePurchaseHistory() {
  const [history, setHistory] = useState<PurchaseHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPurchaseHistory = useCallback(
    async (page: number = 1, pageSize: number = 50) => {
      setLoading(true);
      setError(null);
      try {
        const payload = {
          searchTerm: "",
          searchFields: [],
          fuzzy: false,
          filters: {},
          sort: [{ field: "createdAt", order: "desc" }],
          pagination: { page, pageSize },
        };
        const data = await subscriptionService.getPurchaseHistory(payload);
        setHistory(data);
        return data;
      } catch (err: any) {
        const errorMsg = err?.message || "Error fetching purchase history";
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    history,
    loading,
    error,
    fetchPurchaseHistory,
  };
}
